import { createContext, useContext, useState, useCallback, useEffect, startTransition } from 'react';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const queryClient = useQueryClient();

  // Helper function to check if we're on login page
  const isOnLoginPage = () => {
    return currentPath === '/login';
  };

  // Listen to URL changes
  useEffect(() => {
    const handlePathChange = () => {
      setCurrentPath(window.location.pathname);
    };

    // Listen to browser navigation events
    window.addEventListener('popstate', handlePathChange);

    // Also check on initial load
    setCurrentPath(window.location.pathname);

    return () => {
      window.removeEventListener('popstate', handlePathChange);
    };
  }, []);

  // Helper function to format user data consistently
  const formatUserData = (userData) => {
    if (!userData) return null;

    // Ensure we have the required fields
    if (!userData.id || !userData.email) {
      console.error('Missing required user data fields:', userData);
      return null;
    }

    return {
      id: userData.id,
      email: userData.email,
      role: userData.role || 'user',
      firstName: userData.first_name || userData.firstName || '',
      lastName: userData.last_name || userData.lastName || '',
      createdAt: userData.created_at || userData.createdAt,
      lastLogin: userData.last_login || userData.lastLogin,
      is_active: userData.is_active !== undefined ? userData.is_active : userData.isActive
    };
  };

  // Helper function to update auth state
  const updateAuthState = (userData, token = null) => {
    try {
      const formattedUser = userData ? formatUserData(userData) : null;
      setUser(formattedUser);
      setIsAuthenticated(!!formattedUser);

      if (formattedUser && token) {
        try {
          const decodedToken = jwtDecode(token);
          setTokenExpiry(decodedToken.exp * 1000);
        } catch (error) {
          console.error('Error decoding token:', error);
          setTokenExpiry(null);
        }
      } else {
        setTokenExpiry(null);
      }

      // Invalidate all queries to ensure fresh data is loaded
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Error updating auth state:', error);
      setUser(null);
      setIsAuthenticated(false);
      setTokenExpiry(null);
    }
  };

  // Check if user is authenticated
  const checkAuth = useCallback(async () => {
    // Skip auth check if on login page
    if (isOnLoginPage()) {
      setIsLoading(false);
      return;
    }

    try {
      // Get current user data - httpOnly cookies will be sent automatically
      const response = await api.get('/auth/me', { showToast: false });

      if (!response.data || !response.data.data) {
        throw new Error('No data received');
      }

      // Handle nested user object
      const userData = response.data.data.user || response.data.data;

      if (!userData || !userData.id) {
        throw new Error('Invalid user data format');
      }

      startTransition(() => {
        // Mark that we've fetched the profile to prevent additional calls
        userData.profileFetched = true;
        updateAuthState(userData, null); // Don't pass token since we can't read httpOnly cookies
        setIsLoading(false);
      });
    } catch (error) {
      if (error.response?.status === 401) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          startTransition(() => {
            updateAuthState(null);
            setIsLoading(false);
          });
        } else {
          // Retry the auth check after successful refresh
          setTimeout(() => checkAuth(), 100);
        }
      } else {
        // For any other error, clear auth state
        startTransition(() => {
          updateAuthState(null);
          setIsLoading(false);
        });
      }
    }
  }, []);

  // Refresh token
  const refreshToken = async () => {
    try {

      // Don't send refreshToken in body - backend will read from httpOnly cookie
      const response = await api.post('/auth/refresh-token', {}, { showToast: false });

      const responseData = response.data.data;
      if (!responseData) {
        return false;
      }

      return true;
    } catch (error) {

      // Since we can't manually clear httpOnly cookies, let the backend handle logout
      try {
        await api.post('/auth/logout', {}, { showToast: false });
      } catch (logoutError) {
        console.error('Error during logout:', logoutError);
      }

      return false;
    }
  };

  // Initialize auth state when the app loads
  useEffect(() => {
    // Only check auth if not on login page
    if (!isOnLoginPage()) {
      checkAuth();
    } else {
      setIsLoading(false);
    }
  }, [checkAuth, currentPath]);

  // Login user
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });

      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from server');
      }

      const { token, refreshToken, user } = response.data.data;

      if (!token || !refreshToken || !user) {
        throw new Error('Missing authentication data');
      }

      // Check if user is active
      if (user.is_active === 0 || user.is_active === false) {
        throw new Error('account_deactivated');
      }

      // Backend has already set httpOnly cookies, just update state
      updateAuthState(user, null);
      return { success: true, user };
    } catch (error) {
      console.error('Login error:', error);

      // Show appropriate error message based on the error
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.message);
      }
      else if (error.message === 'account_deactivated' ||
        (error.response?.data?.message && error.response.data.message.includes('deactivated'))) {
        // Handle deactivated user account
        toast.error(`${email} is temporarily deactivated.`);
      }
      else if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Invalid email or password');
      }
      else if (error.message && error.message.includes('No refresh token available')) {
        // This is likely an invalid credentials error
        toast.error('Invalid email or password');
      }
      else {
        toast.error(error.message);
      }
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Invalid email or password'
      };
    }
  };

  // Logout
  const logout = async () => {
    // Clear client-side state FIRST and synchronously to prevent flash
    setUser(null);
    setIsAuthenticated(false);
    setTokenExpiry(null);
    setIsLoading(false);

    // call logout api

    try {
      await api.post('/auth/logout', {}, { showToast: false });
    } catch (error) {
      console.error('Error during logout API call:', error);
    }

    // Clear API service auth tokens and headers immediately
    if (typeof api.clearAuthToken === 'function') {
      api.clearAuthToken();
    }

    // Clear all query cache immediately
    queryClient.clear();

    // Clear any remaining local storage items (except theme preference)
    try {
      // Get current theme preference before clearing
      const themePreference = localStorage.getItem('themePreference');

      // Clear localStorage except theme
      const keysToKeep = ['themePreference'];
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      // Restore theme preference if it existed
      if (themePreference) {
        localStorage.setItem('themePreference', themePreference);
      }
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }

    // Clear session storage
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }

    // Update the current path to login immediately
    setCurrentPath('/login');

    // Show success message
    toast.success('Logged out successfully');

    // Redirect immediately using window.location.replace (no history entry)
    window.location.replace('/login');

    // Call logout endpoint to clear httpOnly cookies on backend (in background)
    try {
      await api.post('/auth/logout', {}, { showToast: false });
    } catch (error) {
      console.error('Error during logout API call:', error);
      // Even if backend call fails, user is already redirected
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      if (!user || !user.id) {
        throw new Error('User not authenticated');
      }

      const response = await api.put(`/users/${user.id}`, userData);

      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from server');
      }

      // Update auth state with new data
      updateAuthState({
        ...user,
        ...response.data.data
      });

      toast.success('Profile updated successfully');
      return { success: true, user: response.data.data };
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.response?.data?.message || error.message || 'Profile update failed');
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Profile update failed'
      };
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      if (!user || !user.id) {
        throw new Error('User not authenticated');
      }

      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword: newPassword
      });

      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.response?.data?.message || error.message || 'Password change failed');
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Password change failed'
      };
    }
  };

  // Fetch current user profile
  const fetchUserProfile = async () => {
    try {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }

      const response = await api.get('/auth/me');

      if (!response.data || !response.data.data) {
        throw new Error('Invalid response from server');
      }

      // Handle nested user object
      const userData = response.data.data.user || response.data.data;

      if (!userData || !userData.id) {
        throw new Error('Invalid user data format');
      }

      // Update auth state with fresh data
      updateAuthState(userData);
      return { success: true, user: userData };
    } catch (error) {
      console.error('Fetch user profile error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch user profile'
      };
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    tokenExpiry,
    checkAuth,
    login,
    logout,
    updateProfile,
    changePassword,
    fetchUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
